import { type Draft, produce } from 'immer';
import { IOGateway } from '../../../gateway';
import { forwardUpdate } from '../../common/context/ops.ts';
import type { UpdateContext } from '../../common/context/types/messages.ts';
import { error, isLocalSource, success } from '../../common/messages.ts';
import type { PeerMessage, RequestMessage } from '../../common/types/messages.ts';
import type { PartialBy } from '../../common/utilities.ts';
import type { Domain, HandleReturnType, StateToMessagesReturnType } from '../../domain.ts';
import { getLogger } from '../../logger.ts';
import type {
    ActivityIncomingType,
    CommandType,
    DomainType,
    MessageType,
    NodeMessage,
    SendMessageAction
} from '../../node.ts';
import { exceptionToReason, type Reason, reason, requestToReason } from '../../reason.ts';
import * as rst from '../../restrictions.ts';
import {
    inDomain,
    joinDomain,
    leaveDomain as stateLeaveDomain,
    removeGatewayRequest,
    threadStateAndActions
} from '../../state/core.ts';
import {
    announcePeer,
    byIdInDomain,
    byIdInDomainThrows, byIdThrows,
    bySourceInDomain,
    type JoinedPeer,
    leaveDomain,
    setPeer
} from '../../state/peers.ts';
import * as activity from '../../state/types/activity.ts';
import type { NodeAddress, Source } from '../../state/types/common.ts';
import type { DomainInfo } from '../../state/types/domain-registry.ts';
import { DomainKeys, type Peer, type State } from '../../state/types/state.ts';
import type { DomainJoin, DomainLeave } from '../global/types/messages.ts';
import * as activities from './activities.ts';
import { activityDomainUri, activityUnhandledMessage, failure, reasonPeerRemoved } from './constants.ts';
import * as factories from './factories.ts';
import type {
    ActivityError,
    AddPeerFactories,
    AddTypes,
    CreateActivity,
    CreatePeer,
    DestroyActivity,
    DestroyPeer,
    JoinActivity,
    LeaveActivity,
    Ready,
    Reload,
    RemovePeerFactories,
    Subscribe,
    Unsubscribe
} from './types/messages.ts';
import { disposePeer } from './messages.ts';
import type { DestroyPeerRequest } from './types/requests.ts';

const log = getLogger('gateway.domains.activity');

function destroyPeer(state: State, source: Source, request: DestroyPeerRequest): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, destroy_peer_id: destroyPeerId} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    const target = byIdThrows(state, destroyPeerId);
    return [state, [
        disposePeer(target.source, destroyPeerId, peerId, requestToReason(request)),
        success(activityDomainUri, source, requestId, peerId)
    ]];
}

export function handleError(state: State, source: Source, request: RequestMessage & {reason: string, reason_uri: string}): HandleReturnType {
    const {state: stateWithoutR, removed: gatewayR} = removeGatewayRequest(state, request.request_id);
    if (gatewayR) {
        const type = gatewayR.type;
        switch (type) {
            case 'activity': {
                const activity = gatewayR.activity;
                if (activity) {
                    return activities.handleError(stateWithoutR, activity);
                }
                break;
            }
            case 'create-peer': {
                return factories.handleCreatePeerError(stateWithoutR, requestToReason(request), gatewayR.clientRequest, gatewayR);
            }
            default: {
                log.error('Unable to handle error for an unknown incoming request type ' + type);
                break;
            }
        }
    }
    return [state, []];
}


/*Handles the case where a peer has joined in a response of a gateway request*/
function joinGwRequest(state: State, peer?: Peer): State {
    const creationRequest = peer?.creationRequest;
    if (creationRequest) {
        const {peer_name: peerName, peer_type: peerType} = creationRequest;
        peer = produce(peer!, (d: Draft<Peer>) => {
            if (peerName) {
                d.peerName = peerName;
            }
            if (peerType) {
                d.peerType = peerType;
            }
        });
        switch (creationRequest.type) {
            case 'activity': {
                return activities.activityMemberJoined(state, creationRequest.activity?.id, peer, creationRequest);
            }
            case 'create-peer': {
                return setPeer(state, peer.id, peer);
            }
        }
    }
    return state;
}

/*Peer is joining the domain.*/
export function forwardJoin(state: State, source: Source, request: PartialBy<DomainJoin, 'type' | 'destination'>): HandleReturnType {

    const {request_id: requestId, peer_id: peerId, restrictions} = request;
    if (inDomain(state, peerId, activity.activityDomain)) {
        // peer already in the domain
        return [state, [success(activityDomainUri, source, requestId, peerId)]];
    }
    // join it in
    const parsedRestrictions = rst.parse(restrictions);
    state = joinDomain(state, peerId, activity.activityDomain, parsedRestrictions);

    // todo do we throw here?
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Activity);

    state = joinGwRequest(state, peer);
    let messages = new Array<SendMessageAction<MessageType>>;
    messages = messages.concat(announcePeer(activityDomainUri, activity.activityDomain, state, source, peer));
    messages = messages.concat(activities.announceTypes(state, peer));
    messages = messages.concat(factories.announceExisting(state, peer));
    // messages = messages.concat(activities.announceExisting(state, peer));
    messages = messages.concat([success(activityDomainUri, source, requestId, peerId)]);
    return [state, messages];

}

/**
 * Peer is leaving the domain - deal with activities and factories
 */
function removePeer(state: State, peer: JoinedPeer<DomainKeys.Activity>, reason: Reason, isSourceRemoved: boolean): HandleReturnType {
    return threadStateAndActions(state,
        (s) => activities.removePeer(s, peer, reason),
        (s) => factories.removePeer(s, peer),
        (s) => leaveDomain(activityDomainUri, activity.activityDomain, s, peer, reason, isSourceRemoved));
}

function forwardLeave(state: State, source: Source, request: DomainLeave): HandleReturnType {
    const {request_id: requestId, peer_id: peerId} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    return threadStateAndActions(state,
        (s) => removePeer(s, peer, requestToReason(request), false),
        (s) => [s, [success(activityDomainUri, source, requestId, peerId)]]);
}

export function ready(state: State, source: Source, request: PeerMessage): HandleReturnType {
    const peerId = request.peer_id;
    const peer = byIdInDomain(state, peerId, DomainKeys.Activity);
    const activityId = peer?.[activity.activityDomain].member;
    if (activityId) {
        return activities.peerReady(state, source, activityId, peer);
    } else {
        return factories.peerReady(state, peer);
    }

}
/**
 * Source has disconnected from the system
 */
export function sourceRemoved(state: State, source: Source): HandleReturnType {
    if (log.enabledFor('debug')) {
       log.debug(`removing source ${JSON.stringify(source)} from activity domain`);
    }
    const affected = bySourceInDomain(state, source, activity.activityDomain);
    const stateWithoutAffected = affected
        .map(p => p.id)
        .reduce((s, peerId) => {
            return stateLeaveDomain(s, peerId, activity.activityDomain);
        }, state);
    const r: HandleReturnType = affected.reduce(([s, m], peer) => {
        const [ns, nm] = removePeer(s, peer, reasonPeerRemoved, true);
        return [ns, m.concat(nm)];
    }, [stateWithoutAffected, []] as HandleReturnType);
    if (log.enabledFor('debug')) {
        log.debug(`removed source ${JSON.stringify(source)} from activity domain`);
    }
    return r;
}

export function handleRequest(state: State, source: Source, request: IOGateway.Message<ActivityIncomingType | DomainType | CommandType>): HandleReturnType {
    switch (request.type) {
        case 'domain/join': {
            return forwardJoin(state, source, request as DomainJoin);
        }
        case 'domain/leave': {
            return forwardLeave(state, source, request as DomainLeave);
        }
        case 'ready': {
            return ready(state, source, request as Ready);
        }
        case 'add-types': {
            const {request_id: requestId, peer_id: peerId, types} = request as AddTypes;
            return activities.addTypes(state, source, requestId, peerId, types);
        }
        case 'remove-types': {
            return activities.removeTypes(state, source, request);
        }
        case 'create': {
            // todo ttl
            const {configuration, ...rq} = request as CreateActivity;
            return activities.createActivity(state, source, rq, configuration)
        }
        case 'destroy': {
            return activities.forwardDestroyActivity(state, source, request as DestroyActivity);
        }
        case 'subscribe': {
            return activities.subscribe(state, source, request as Subscribe);
        }
        case 'unsubscribe': {
            return activities.unsubscribe(state, source, request as Unsubscribe);
        }
        case 'join-activity': {
            return activities.joinActivity(state, source, request as JoinActivity);
        }
        case 'leave-activity': {
            return activities.leaveActivity(state, source, request as LeaveActivity);
        }
        case 'add-peer-factories': {
            return factories.add(state, source, request as AddPeerFactories);
        }
        case 'remove-peer-factories': {
            return factories.removeFactories(state, source, request as RemovePeerFactories);
        }
        case 'create-peer': {
            // todo ttl
            return factories.create(state, source, request as CreatePeer);
        }
        case 'destroy-peer': {
            return destroyPeer(state, source, request as DestroyPeer);
        }
        case 'reload': {
            return activities.reload(state, source, request as Reload);
        }
        case 'error': {
            return handleError(state, source, request as ActivityError);
        }
        case 'update-context': {
            return forwardUpdate(activityDomainUri, state, source, request as UpdateContext);
        }
        case 'commands/source-removed': {
            return sourceRemoved(state, source);
        }
        default: {
            log.error(`Unhandled message ${JSON.stringify(request)}`);
            return [state, [error(activityDomainUri, source, request['request_id'] ?? -1, request['peer_id'], reason(activityUnhandledMessage, `Unhandled message ${JSON.stringify(request)}`))]]
        }
    }
}

class ActivityDomain implements Domain<ActivityIncomingType> {
    info(): DomainInfo {
        return {
            uri: activityDomainUri,
            description: '',
            version: 2
        };
    }

    init(state: State): State {
        return state;
    }

    destroy(state: State): State {
        return state;
    }

    handleMessage(state: State, request: NodeMessage<CommandType | DomainType | ActivityIncomingType>): HandleReturnType {
        const {source, body} = request;
        try {
            return handleRequest(state, source, body);
        } catch (e) {
            if (isLocalSource(source)) {
                return [state, [error(activityDomainUri, source, body['request_id'], body['peer_id'], exceptionToReason(e, failure))]]
            } else {
                return [state, []]
            }
        }
    }

    stateToMessages(_state: State, _receiver: NodeAddress): StateToMessagesReturnType {
        return [];
    }

}

export function activityDomain(): ActivityDomain {
    return new ActivityDomain();
}
